import React, { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Home, MessageCircle, MapPin, Bell, User, LayoutDashboard, Sparkles, MailWarning, Inbox, ScanLine, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/api";
import VerifyEmailModal from "@/components/VerifyEmailModal";

function navItemsFor(role, isAdmin) {
  const items = [
    { to: "/app", label: "Home", icon: Home, end: true },
    { to: "/app/chat", label: "Ask AI", icon: MessageCircle },
    { to: "/app/scan", label: "Scan", icon: ScanLine },
    { to: "/app/breed-identifier", label: "Breed ID", icon: PawPrint },
    { to: "/app/vets", label: "Vets", icon: MapPin },
  ];
  if (role === "vet") {
    items.push({ to: "/app/vet-dashboard", label: "Dashboard", icon: LayoutDashboard });
  } else {
    items.push({ to: "/app/reminders", label: "Reminders", icon: Bell });
  }
  if (isAdmin) {
    items.push({ to: "/app/leads", label: "Leads", icon: Inbox });
  }
  items.push({ to: "/app/profile", label: "Profile", icon: User });
  return items;
}

export default function AppLayout() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const handledSession = useRef(false);
  const [showVerify, setShowVerify] = useState(false);
  const items = navItemsFor(user?.role, user?.is_admin);

  // Handle Stripe checkout return (?session_id=...) redirected to app root
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || handledSession.current) return;
    handledSession.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const res = await apiFetch(`/billing/status/${sessionId}`);
        if (res.payment_status === "paid") {
          await refresh();
          toast.success("Payment confirmed — your subscription is active! 🎉");
          const next = new URLSearchParams(searchParams);
          next.delete("session_id");
          setSearchParams(next, { replace: true });
          return;
        }
      } catch {
        /* ignore & retry */
      }
      if (attempts < 15) setTimeout(poll, 2500);
      else {
        const next = new URLSearchParams(searchParams);
        next.delete("session_id");
        setSearchParams(next, { replace: true });
      }
    };
    poll();
  }, [searchParams, setSearchParams, refresh]);

  const showUpgrade = user?.role !== "vet";

  return (
    <div className="min-h-screen bg-[#FCFBF8] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FCFBF8]/80 border-b border-[#E8E3DA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Logo size={34} />
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
                    isActive
                      ? "bg-[#E86A45] text-white shadow-sm"
                      : "text-[#4A4746] hover:bg-[#F2EFE9]"
                  }`
                }
              >
                <it.icon size={17} />
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {showUpgrade && (
              <button
                data-testid="header-upgrade-btn"
                onClick={() => navigate("/app/subscription")}
                className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-[#F6DED5] text-[#B54828] hover:bg-[#f3d0c2] transition-colors"
              >
                <Sparkles size={16} /> Go Pro
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Email verification banner */}
      {user?.email_verified === false && (
        <div className="bg-[#FBEED9] border-b border-[#F0D9A8]" data-testid="verify-email-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <MailWarning size={18} color="#B7791F" className="shrink-0" />
            <p className="text-sm font-semibold text-[#8a5a12] flex-1">
              Verify your email to unlock AI health chat.
            </p>
            <button
              data-testid="verify-email-banner-btn"
              onClick={() => setShowVerify(true)}
              className="text-sm font-extrabold text-white bg-[#E86A45] rounded-full px-4 py-1.5 hover:bg-[#d65f3c] transition-colors shrink-0"
            >
              Verify now
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-10">
        <Outlet context={{ openVerify: () => setShowVerify(true) }} />
      </main>

      {/* Bottom tab bar (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-white/90 border-t border-[#E8E3DA] flex items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]">
        {items.map((it) => {
          const active = it.end
            ? location.pathname === it.to
            : location.pathname.startsWith(it.to);
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`tab-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5"
            >
              <it.icon size={22} color={active ? "#E86A45" : "#9A938B"} />
              <span
                className="text-[10px] font-bold"
                style={{ color: active ? "#E86A45" : "#9A938B" }}
              >
                {it.label}
              </span>
            </NavLink>
          );
        })}
      </nav>

      <VerifyEmailModal open={showVerify} onClose={() => setShowVerify(false)} />
    </div>
  );
}
